import { createNlsMiddleware, lccstate, searchOpenProcess, userstate } from 'lcc-state';
import { logSystem } from 'logger';
import { publish, subscribe } from 'pub-sub';
import has from '@dojo/framework/core/has';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DNode } from '@dojo/framework/core/interfaces';
import { createBreakpointMiddleware } from '@dojo/framework/core/middleware/breakpoint';
import { focus } from '@dojo/framework/core/middleware/focus';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { create, v, w } from '@dojo/framework/core/vdom';
import Avatar from '@dojo/widgets/avatar';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { theme } from '@dojo/widgets/middleware/theme';
import Icon from '@holmescorp/base-widgets/icon';
import { MenuItem } from '@holmescorp/lcc-state/interfaces';

import css from '../css/heading.m.css';

// eslint-disable-next-line @typescript-eslint/no-unused-vars

interface HeadingIcache {
	onlinestate: boolean;
	useSearch: boolean;
	height: number;
	menuOpen: boolean;
	hasNotifications: boolean;
	notificationArea: string;
	//
	ttExplore: boolean;
	ttToolsandtips: boolean;
	ttGlossary: boolean;
	ttSearch: boolean;
	ttSettings: boolean;
	ttExamCountdownOpen: boolean;
	ttHome: boolean;
	//
	focusNode: string;
}

export interface HeadingProperties {
	showNav?: boolean;
}

const breakpoint = createBreakpointMiddleware({ mobile: 0, fullsize: 769 });
const icache = createICacheMiddleware<HeadingIcache>();
const nls = createNlsMiddleware('settings');
const factory = create({
	icache,
	theme,
	nls,
	lccstate,
	userstate,
	breakpoint,
	focus
}).properties<HeadingProperties>();
export const AlternateHeading = factory(
	({ middleware: { icache, theme, nls, lccstate, breakpoint, focus, userstate } }) => {
		const onlinestate = icache.getOrSet('onlinestate', false);
		const { get, path } = lccstate;
		const { get: uget, path: upath } = userstate;

		const online = get(path('online'));
		const product = get(path('product'));
		const mainmenu: MenuItem[] = get(path('mainmenu'));
		const bp = breakpoint.get('header');
		const isMobile = bp && bp.breakpoint === 'mobile';

		const currentLocation = uget(upath('location'));
		const userFullname = uget(upath('fullname'));
		const userInitials =
			userFullname
				.split(' ')
				.map((name: string) => name[0].toUpperCase())
				.join('') || w(Icon, { faname: 'user' });

		if (onlinestate !== online) {
			icache.set('onlinestate', online);
			logSystem.info(() => `${online ? 'WS online' : 'WS offline'}`);
		}

		const { isPlaceholder } = nls();

		if (isPlaceholder) {
			return null;
		}

		const themeCss = theme.classes(css);

		/* images need CDN */
		const mobile = bp && bp.breakpoint === 'mobile' ? '-mobile' : '';
		const headerImageExtension = has('use-svg-header-image') ? '.svg' : '.png';
		const productimg = `${window.hcstatic.portal_cdn}/js/images/${product}${mobile}${headerImageExtension}`;
		/* title is the product image */
		const title = v('img', {
			classes: [themeCss.product],
			...formatAriaProperties({ label: product }),
			role: 'img',
			xqa: 'product',
			alt: product,
			src: productimg,
			onclick: (event) => {
				event && event.stopPropagation();
				publish('site/homepage');
			}
		});

		if (mainmenu.length === 0) {
			mainmenu.push({
				pane: { widget: 'GlossaryPane', params: {} },
				icon: { action: 'glossary/open', faname: 'briefcase', altText: 'glossary', ttText: 'Glossary' }
			});
			mainmenu.push({
				pane: { widget: 'SettingsPane', params: {} },
				icon: { action: 'settings/open', faname: 'cogs', altText: 'settings', ttText: 'Settings' }
			});
		}
		subscribe('search/open', () => {
			lccstate.run(searchOpenProcess)({ searchOpen: true });
		});
		const menupanes: DNode[] = [
			// add standard panes without menu items
			w('VideoPane', { videonum: '1', openaction: 'video/open' }),
			w('CertPane', {}),
			w('EvalPane', {}),
			w('MobileNavPane', {})
		];

		// filter out splash items (temporarily stored in mainmenu until rpc returns outside of mainmenu)
		mainmenu
			.filter((item) => !('splash' in item))
			.forEach((item) => {
				const { pane } = item;

				if (pane) {
					const { widget, params } = pane;
					menupanes.push(
						w(
							widget,
							Object.assign(
								{
									onClose: () => {
										focus.focus();
									}
								},
								params
							)
						)
					);
				}
			});

		const logoTitle = v('div', { classes: [themeCss.logo] }, [
			v('div', { classes: [themeCss.title], key: 'title', xqa: 'nav-logo-title' }, [title])
		]);

		const searchBox = v('div', { classes: [themeCss.primary] }, [
			v(
				'div',
				{
					classes: [themeCss.search],
					key: 'search',
					xqa: 'nav-search-button',
					onclick: () => publish('search/open')
				},
				[w(Icon, { faname: 'search', size: 'lg' })]
			)
		]);

		// TODO: add mobile navigation actions
		const navigation = isMobile
			? v(
					'div',
					{
						onclick: () => publish('mobile-nav/open'),
						xqa: 'nav-mobile-open-button'
					},
					[w(Icon, { faname: 'bars' })]
			  )
			: v('div', { classes: [themeCss.primary, themeCss.nav] }, [
					v(
						'span',
						{
							xqa: 'nav-home-button',
							classes: [currentLocation === '/explore' ? themeCss.active : '', themeCss.navItem],
							onclick: () => publish('site/homepage')
						},
						['Home']
					),
					v(
						'span',
						{
							xqa: 'nav-my-progress-button',
							classes: [currentLocation === '/my-progress' ? themeCss.active : '', themeCss.navItem],
							onclick: () => publish('site/progress')
						},
						['My Progress']
					)
			  ]);

		const user = v('div', { classes: [themeCss.primary] }, [
			v(
				'span',
				{
					classes: [themeCss.avatar, themeCss.navItem],
					xqa: 'avatar',
					onclick: () => publish('settings/open')
				},
				[w(Avatar, { size: 'large' }, [userInitials])]
			)
		]);

		const hcConnectBar = () => {
			const arrowImageExtension = has('use-svg-header-image') ? '.svg' : '.png';
			const arrowImage = `${window.hcstatic.portal_cdn}/js/images/icons/hc-connect-back-arrow${arrowImageExtension}`;
			return v(
				'div',
				{
					classes: [themeCss.hcConnectBar],
					xqa: 'hc-connect-bar',
					key: 'hc-connect-bar'
				},
				[
					v(
						'a',
						{
							href: 'https://myhcconnect.com',
							classes: [themeCss.hcConnectLink],
							xqa: 'hc-connect-link',
							...formatAriaProperties({ label: 'HC Connect' })
						},
						[
							v('img', {
								src: arrowImage,
								alt: 'HC Connect back arrow',
								role: 'img'
							}),
							v('span', {}, ['HC Connect'])
						]
					)
				]
			);
		};

		return [
			v(
				'header',
				{
					key: 'header',
					role: 'banner',
					xqa: 'heading',
					classes: [theme.variant(), themeCss.root]
				},
				[
					v('div', { classes: [themeCss.heading], key: 'root' }, [
						isMobile
							? v('div', { classes: [themeCss.row] }, [navigation, logoTitle, searchBox, user])
							: v('div', { classes: [themeCss.row] }, [
									logoTitle,
									v('div', { classes: [themeCss.actions] }, [navigation, searchBox, user])
							  ])
					]),
					...menupanes
				]
			),
			has('use-hc-connect') ? hcConnectBar() : null
		];
	}
);
